
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { find } from 'lodash';
import Historique from './historique.vue';
import { DevisApi } from '@/api/devis';
import CarouselArticles from './carousel-articles.vue';
import VisionMarche from './vision-marche.vue';
import { ParamsCommande } from '@/model/params-commande';
import { Devis } from '@/model/devis';
import { RouteName } from '@/router/routes';
import { FicheArticle } from '@/model/fiche-article';
import { getQuantiteUnite } from '@/utils/conditionnement';
import { Conditionnement } from '@/model/conditionnement';
import { ModeReglement } from '@/model/mode-reglement';
import { required, withMessage, custom } from 'form-validation-manager';
import { getYear, addDays, startOfDay, addYears, isBefore, startOfMonth, addMonths, format } from 'date-fns';
import { CriteresDevis } from '@/model/criteres-devis';

import localeFr from 'date-fns/locale/fr';
import { Roles } from '@/utils/auth/roles';

export default Vue.extend({
  name: 'DetailArticle',
  components: {
    Historique,
    CarouselArticles,
    VisionMarche
  },
  props: {
    idArticle: {
      type: Number,
      required: true
    },
    region: {
      type: String,
      required: false
    }
  },
  data: () => ({
    loading: true as boolean,
    paramsCommande: null as ParamsCommande,
    form: {
      quantite: null as number,
      livraison: null as string,
      paiement: {
        reglement: null
      }
    },
    devisBtnText: 'Demander un devis',
    alreadySendDevis: false,
    dateLivraison: null as Date,
    showDevis: false as boolean,
    listeMoisLivr: null as Date[],
    listeMoisLivrTexte: null as string[]

  }),
  validations: {
    form: {
      quantite: custom(function () {
        return this.quantiteTonne > this.ficheArticle.stock.valeur ? 'Stock insufisant' : false;
      }),
      paiement: {
        reglement: withMessage(required(), 'Veuillez sélectionner un mode de paiement')
      }
    }
  },
  computed: {
    ...mapGetters({
      articles: 'article/listeArticle',
      regionSelectionnee: 'user/regionSelectionnee',
      tiers: 'tiers/infosTiers',
      user: 'user/user',
      isWebClosed: 'referentiel/isWebClosed'
    }),
    ficheArticle (): FicheArticle {
      return find(this.articles as FicheArticle[], f => f.article.id === this.idArticle);
    },
    conditionnement (): Conditionnement {
      return this.paramsCommande?.conditionnements[0];
    },
    btnCommanderDisabled (): boolean {
      return !this.hasRole(this.Roles.USER_ADHERENT) || this.isWebClosed || !this.ficheArticle.stock.stockDisponible || this.ficheArticle.stock.valeur == null;
    },
    btnDevisDisabled (): boolean {
      return !this.hasRole(this.Roles.USER_ADHERENT);
    },
    quantiteTonne (): number {
      if (!this.conditionnement) return 0;
      return getQuantiteUnite(this.form.quantite, this.conditionnement, 't');
    },
    modesReglement (): ModeReglement[] {
      return this.paramsCommande?.modesReglement || [];
    }
  },
  watch: {
    idArticle () {
      this.init();
    },
    regionSelectionnee () {
      if (!this.hasRole(this.Roles.USER_ADHERENT)) {
        this.loadListeArticle(this.regionSelectionnee ? this.regionSelectionnee.codeRegion : null);
      }
    }
  },
  created () {
    this.init();
  },

  methods: {
    ...mapActions({
      loadListeArticle: 'article/loadListeArticle',
      loadParamsCommandeArticle: 'article/loadParamsCommandeArticle',
      ajouterItemPanier: 'panier/ajouterItemPanier'
    }),
    test () {
      this.ajouterItemPanier({});
      this.$dialog.confirm({
        text: '1 article ajouté à votre panier',
        actions: {
          false: {
            text: 'Continuer mes achats',
            flat: false,
            outlined: false,
            color: 'primary'
          },
          true: {
            text: 'Voir mon panier',
            flat: false,
            outlined: false,
            color: 'primary'
          }
        }
      }).then((response) => {
        if (response) {
          this.$router.push({ name: RouteName.MAIN_PANIER });
        }
      });
    },

    init () {
      this.loading = true;
      this.form.quantite = null;

      Promise.all([

        this.articles && this.articles.length === 0 ? Promise.resolve() : this.loadListeArticle(this.regionSelectionnee ? this.regionSelectionnee.codeRegion : null)
      ]).then(() => {
        if (this.hasRole(Roles.USER_ADHERENT)) {
          this.loadParamsCommandeArticle(this.idArticle).then(paramsCommande => {
            this.paramsCommande = paramsCommande;
            this.canSendDevis();
            this.initForm();
            this.moisLivraison();
          });
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    capitalize (str: string) {
      return str?.toLowerCase().replace(/(\W|^)\w/g, c => c.toUpperCase());
    },
    getComment (): string | null {
      const numArticle = this.ficheArticle.article.numArticle;
      if (['1001980', '1001970'].includes(numArticle)) {
        return 'Prix €/t vrac logé franco';
      } else if (['0002477', '1001121'].includes(numArticle)) {
        return 'Prix €/t vrac départ port';
      } else if (['1001037'].includes(numArticle)) {
        return 'Prix €/t vrac départ Rouen';
      } else {
        return null;
      }
    },
    envoyerDevis () {
      const criteres = new CriteresDevis();

      criteres.quantite = this.form.quantite;
      criteres.tonnage = this.quantiteTonne;
      criteres.reglement = (this.form.paiement.reglement ? this.form.paiement.reglement.libelle : null);
      criteres.moisLivraison = this.form.livraison;

      this.alreadySendDevis = true;
      this.devisBtnText = 'Envoi en cours ...';

      DevisApi.creerDemandeDevis(this.ficheArticle.article.id, criteres).then(() => {
        this.devisBtnText = 'Demande de devis envoyée';
        this.$dialog.notify.success('Votre demande de devis a bien été prise en compte');
        this.showDevis = false;
      });
    },
    canSendDevis () {
      DevisApi.getDevisOfDayByArticleAndUser(this.ficheArticle.article.id, this.user.username).then((devis: Devis[]) => {
        if (devis.length > 0) {
          this.alreadySendDevis = true;
          this.devisBtnText = 'Demande de devis déjà envoyée';
        } else {
          this.alreadySendDevis = false;
          this.devisBtnText = 'Demander un devis';
        }
      });
    },
    openPopup () {
      this.showDevis = true;
    },
    closePopup () {
      this.showDevis = false;
    },

    initForm () {
      this.form.quantite = 1;

      // this.form.paiement.reglement = find(this.modesReglement, 'PQ');
    },
    moisLivraison (): Date[] {
      this.listeMoisLivr = [];
      this.listeMoisLivrTexte = [];

      const today = startOfDay(Date.now());
      const day45j = addDays(today, 45);

      const anneeString = getYear(day45j);
      const dt = anneeString + '-06-01T00:00';
      const maiAnneeN = new Date(dt);
      const maiAnneeNS = addYears(maiAnneeN, 1);

      if (isBefore(day45j, maiAnneeN)) {
        let debutMois = startOfMonth(day45j);

        while (isBefore(debutMois, maiAnneeN)) {
          this.listeMoisLivr.push(debutMois);
          this.listeMoisLivrTexte.push(format(debutMois, 'MMMM yyyy', { locale: localeFr }));
          const ms = addMonths(debutMois, 1);
          debutMois = ms;
        }
      } else {
        let debutMois = startOfMonth(day45j);

        while (isBefore(debutMois, maiAnneeNS)) {
          this.listeMoisLivr.push(debutMois);
          this.listeMoisLivrTexte.push(format(debutMois, 'MMMM yyyy', { locale: localeFr }));

          const ms = addMonths(debutMois, 1);
          debutMois = ms;
        }
      }
      return this.listeMoisLivr;
    }
  }

});
